// Remembers which member pharmacies (MMIDs) a user has picked on the shared NCPDP dropdown so the
// selection survives navigating between search pages and reloading the browser, the same way the
// selected organization does.
//
// MMIDs are compared as strings throughout. ToLocationOptions already stringifies option.value,
// but values round-trip through JSON storage and the raw payload carries the MMID as a number, so
// normalizing both sides avoids a mismatch that would silently remember nothing.

function ToMmidKey(value) {
  if (value === null || value === undefined) return null;

  const key = String(value).trim();

  return key === "" ? null : key;
}

// Normalizes anything read from storage, context, or a dropdown change into a de-duplicated list
// of MMID strings. Anything unusable collapses to an empty list, which callers read as
// "nothing remembered".
export function ToMmidKeyList(value) {
  if (!Array.isArray(value)) return [];

  const keys = [];

  value.forEach((entry) => {
    const key = ToMmidKey(entry);

    if (key !== null && keys.indexOf(key) === -1) keys.push(key);
  });

  return keys;
}

// Narrows a remembered selection to the options actually on offer and returns the matching option
// objects, because MultiSelectDropdownBody reads option.value off its defaultSelected entries.
//
// Returns an empty list both when nothing is remembered and when nothing survives. Callers treat
// that single result as "select everything", matching ReconcileSelectedRpids.
export function ReconcileSelectedMmids(options, rememberedMmids) {
  if (!Array.isArray(options) || options.length === 0) return [];

  const rememberedKeys = ToMmidKeyList(rememberedMmids);

  if (rememberedKeys.length === 0) return [];

  return options.filter((option) => {
    const key = ToMmidKey(option && option.value);

    return key !== null && rememberedKeys.indexOf(key) > -1;
  });
}

// Folds a dropdown's new selection back into the remembered list.
//
// Only the MMIDs the dropdown could actually show are replaced; anything remembered from a list
// this dropdown does not carry is preserved. The program-specific lists (recLocations,
// metricLocations) are subsets of the full location list, so without this a visit to a Rec 2.0
// page would drop every remembered MMID outside that program. It also means widening a decomposed
// Group's refinement brings back the MMIDs the narrowing had hidden.
export function MergeRememberedMmids(rememberedMmids, options, selectedValues) {
  const selectedKeys = ToMmidKeyList(selectedValues);

  if (!Array.isArray(options)) return selectedKeys;

  const offeredKeys = ToMmidKeyList(options.map((option) => option && option.value));

  const retainedKeys = ToMmidKeyList(rememberedMmids).filter(
    (key) => offeredKeys.indexOf(key) === -1
  );

  return ToMmidKeyList(retainedKeys.concat(selectedKeys));
}

// True when two normalized MMID lists hold the same members, used to keep the context setter from
// re-persisting an unchanged selection and re-rendering every consumer.
export function AreSameMmidLists(first, second) {
  const firstKeys = ToMmidKeyList(first);
  const secondKeys = ToMmidKeyList(second);

  if (firstKeys.length !== secondKeys.length) return false;

  return firstKeys.every((key) => secondKeys.indexOf(key) > -1);
}
